/**
 * Searches the case graph for entities by name (the graph's own entity search) and keeps only
 * entities that carry a stable `_uid` (ADR-0091): nothing picked here is keyed by an element id.
 * Each caller names its own `purpose`, so two pickers on one page keep their own results.
 */
import { useQuery } from '@tanstack/react-query'
import { useState } from 'react'
import type { EntityOption, EntitySearch } from '@/components/entity-picker/EntityPicker'
import { entityUid } from '@/features/entity-history/history-rows'
import { useDebounced } from '@/hooks/use-debounced'
import { graphApi } from '@/services/api/graph'

const MIN_QUERY = 2
const RESULTS = 20

type SearchResult = Awaited<ReturnType<typeof graphApi.searchEntities>>['results'][number]

/** The search's entities that carry a stable id, once each. */
export function entityOptions(results: readonly SearchResult[]): EntityOption[] {
  const options = new Map<string, EntityOption>()
  for (const result of results) {
    const uid = entityUid(result.properties ?? {})
    if (!uid || options.has(uid)) continue
    options.set(uid, { uid, name: result.name ?? uid, label: result.type ?? null })
  }
  return [...options.values()]
}

export function useEntitySearch(caseId: string, purpose: string): EntitySearch {
  const [query, setQuery] = useState('')
  const term = useDebounced(query.trim(), 250)
  const search = useQuery({
    queryKey: ['case', caseId, 'graph', 'search', purpose, term] as const,
    queryFn: async () => {
      const response = await graphApi.searchEntities(caseId, term, { limit: RESULTS })
      return entityOptions(response.results)
    },
    enabled: Boolean(caseId) && term.length >= MIN_QUERY,
    staleTime: 30_000,
  })
  const status: EntitySearch['status'] =
    term.length < MIN_QUERY
      ? 'idle'
      : search.isError
        ? 'failed'
        : search.data && term === query.trim()
          ? 'ready'
          : 'searching'
  return { query, onQueryChange: setQuery, status, results: search.data ?? [] }
}
